import 'core-js';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { initialiseBctApiPermissionReducers } from '@ngt/online-patient-management-bctapi-permissions';
import { RequestState } from '@ngt/request-utilities';
import { SnackbarProvider } from 'notistack';

import { onlinePatientManagement, OnlinePatientManagement } from '@ngt/online-patient-management-ui';

import App from './App';
import * as serviceWorker from './serviceWorker';
import * as Dtos from './api/Neon.Dtos';
import client from './api/client';
import { LOGIN_LINK } from './constants/authentication';
import reducerRegistry from './store/reducerRegistry';
import { Settings as LuxonSettings } from 'luxon';
import { AppSettings } from './AppSettings';
import * as Sentry from "@sentry/react";
import { createSentryAnonymousUser } from './utilities/errors';
import RouteErrorBoundary from './components/Errors/RouteErrorBoundary';

if (AppSettings.sentry.dsn) {
    const integrations = [
        // React Router V6 integration.
        // Helps with tracing URL changes and which URLs belong to the 
        // same route, for example:
        //    - https://test.com/form/1
        //    - https://test.com/form/2
        // both belong to the same route */form/*
        Sentry.reactRouterV5BrowserTracingIntegration({ history: onlinePatientManagement.history }),
        // Browser Tracing integration
        // Helps with tracing URLs and other common browser "spans"
        Sentry.browserTracingIntegration(),
        // Replay integration
        // Records user actions and sends to Sentry.
        Sentry.replayIntegration({
            // Whether to mask all text on the page when recording user actions
            // When false only inputs are masked.
            // There is a separate setting to force unmasked inputs.
            maskAllText: false,
            // Whether to mask all images on the page when recording user 
            // actions.
            blockAllMedia: false,
            // Which URLs can the replay run on.
            // URL must contain one of the strings in the array or match one of
            // the regexes supplied
            networkDetailAllowUrls: [window.location.origin],
            // Capture the body of network requests
            // Sentry will still run data scrubbing on the bodies.
            networkCaptureBodies: true
        })
    ];
    if (AppSettings.sentry.enableReportABug) {
        integrations.push(Sentry.feedbackIntegration({
            // Disable the injection of the default widget
            autoInject: false,
            colorScheme: 'system',
            showBranding: false,
            showName: false,
            showEmail: false,
            tags: {
                Application: AppSettings.sentry.application,
                Customer: AppSettings.sentry.customer
            },
            useSentryUser: {
                name: 'fullName',
                email: 'email',
            },
        }));
    }
    Sentry.init({
        // The DSN to use when sending information to Sentry.
        // Identifies a specific project in Sentry.
        dsn: AppSettings.sentry.dsn,
        // Whether to send identifying information to Sentry such as
        // username, useragent, ip, etc
        sendDefaultPii: AppSettings.sentry.sendDefaultPii,
        // Whether to sent available performance metrics to Sentry.
        // These are the basic performance metrics and not the endepth
        // profiling metrics that take a lot more effort to setup.
        enableMetrics: AppSettings.sentry.enableMetrics,
        // Whether to attach stacktraces to actions sent to Sentry.
        attachStacktrace: AppSettings.sentry.attachStacktrace,
        // Whether @sentry/react is in debug mode, logging more information
        // to the console.
        debug: AppSettings.sentry.debug,
        // initialise integrations.
        integrations: integrations,
        // The environment to associate all action sent to Sentry with.
        environment: AppSettings.sentry.environment,
        // Release to associate all actions sent to Sentry with.
        release: AppSettings.sentry.release,
        // Percentage of captured traces that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        tracesSampleRate: AppSettings.sentry.tracesSampleRate,
        // Which URLs to trace / record in a trace.
        // Only trace internal URLs don't want to trace every SEO url
        // under the sun.
        tracePropagationTargets: [
            'localhost', 
            /^\//,
            new RegExp(`/^${window.location.origin}`)
        ],
        // Percentage of captured session replays that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        replaysSessionSampleRate: AppSettings.sentry.replaysSessionSampleRate,
        // Percentage of captured session replays that are sent to Sentry.
        // when an error occurs
        // 1.0 = 100%, 0 = 0%
        replaysOnErrorSampleRate: AppSettings.sentry.replaysOnErrorSampleRate,
        // Enable logs to be sent to Sentry.
        enableLogs: AppSettings.sentry.enableLogs,
    });
    Sentry.setTag('Application', AppSettings.sentry.application);
    Sentry.setTag('Customer', AppSettings.sentry.customer);
    // Set user to anonymous at start up, this should be overriden by
    // React code if user information is available.
    Sentry.setUser(createSentryAnonymousUser());
}

declare let global: any;

if (typeof global === 'undefined') {
    (window as any).global = window;
}

const user = global.AUTH as Dtos.AuthenticateResponse;
const typeMapping = global.TYPE_MAPPING as Dtos.GetOpmsTypeMappingResponse as any;

if (!user) {
    window.location.href = LOGIN_LINK;
}

//set default timezone to australia/sydney so dates at midnight appear as the correct day for users in other timezones in the world
LuxonSettings.defaultZoneName = 'Australia/Sydney';

onlinePatientManagement.configure({
    typeMapping,
    theme: {
        primaryColour: '#562c85',
        secondaryColour: '#e8078c'
    },
    store: {
        preloadState: {
            authenticatedUser: {
                user,
                loadState: {
                    state: RequestState.Success
                }
            }
        }
    },
    dtos: Dtos,
    reducerRegistry
});

initialiseBctApiPermissionReducers(onlinePatientManagement.reducerRegistry, Dtos, client);

export const store = onlinePatientManagement.store;
export const reduxPromiseListener = onlinePatientManagement.reduxPromiseListener;

ReactDOM.render(
    <OnlinePatientManagement>
        <SnackbarProvider
            maxSnack={1}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
        >
            <Sentry.ErrorBoundary fallback={RouteErrorBoundary}>
                <App hasUser={!!user} />
            </Sentry.ErrorBoundary>
        </SnackbarProvider>
    </OnlinePatientManagement>,
    document.getElementById('root') as HTMLElement
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();